// About — page E-E-A-T dédiée à l'identité geoVINUM.
// Objectif : que ChatGPT/Perplexity/Gemini comprennent la mission, la
// méthodologie, la parenté avec EtOH et les sources en un scroll.
//
// Contenu volontairement factuel (peu de marketing) et structuré en
// sections H2 courtes pour maximiser l'extraction par LLM.
// i18n : dictionnaire inline COPY (FR/EN/ES/PT) — même pattern que ShopProductPage.
import { Link } from "react-router-dom";
import { MapPin, GraduationCap, Database, Sparkles, Building2 } from "lucide-react";
import Layout from "@/components/Layout";
import SEO, {
  geovinumOrganizationJsonLd,
  geovinumWebSiteJsonLd,
} from "@/components/SEO";
import { useLang } from "@/lib/i18n";

const COPY = {
  fr: {
    seo_title: "À propos de geoVINUM",
    seo_desc: "geoVINUM est une plateforme de cartographie viticole, de données et d'apprentissage du vin, éditée par EtOH. Mission, produits, méthodologie et sources.",
    crumb_home: "Accueil",
    crumb_about: "À propos",
    eyebrow: "À propos",
    h1: "geoVINUM — Cartographie, données et connaissance des vignobles du monde",
    lede: "geoVINUM est une plateforme spécialisée dans la cartographie viticole, la géographie du vin, les appellations, les terroirs, les cépages et l'apprentissage du vin. Nous documentons les régions productrices du monde en croisant cartes, fiches éditoriales, parcours pédagogiques et intelligence artificielle.",
    mission_h: "Notre mission",
    mission_p1: "Rendre la géographie du vin lisible pour toute personne curieuse — amateurs, étudiants (WSET Levels 3, 4, 5 · sommellerie · œnologie), professionnels (sommeliers, cavistes, journalistes, exportateurs) et institutions (écoles du vin, domaines, médias).",
    mission_p2_a: "Nous croyons qu'on retient mieux ce qu'on voit : c'est pourquoi",
    mission_p2_b: "la carte est notre porte d'entrée",
    mission_p2_c: ", puis les données et l'IA la prolongent.",
    pillars_h: "Nos cinq piliers",
    p_maps_t: "Maps", p_maps_d: "Cartes viticoles imprimables et téléchargeables, couvrant les principales régions du monde.",
    p_explorer_t: "Explorer", p_explorer_d: "Base éditoriale : fiches d'appellations, de cépages, de producteurs — source de vérité du graphe geoVINUM.",
    p_academy_t: "Academy", p_academy_d: "Parcours d'apprentissage alignés sur les référentiels WSET et sommellerie.",
    p_ai_t: "AI", p_ai_d: "Assistant conversationnel spécialisé vin, ancré sur nos données et cartes.",
    p_data_t: "Interactive / Data", p_data_d: "Cartes intégrables (iframe) et données structurées pour partenaires B2B.",
    method_h: "Notre méthodologie",
    method_1: "Chaque appellation documentée fait l'objet d'une fiche Explorer reliée à sa région parente, ses sous-régions, ses cépages autorisés et ses cartes.",
    method_2: "Les cartes sont dessinées par notre équipe cartographique à partir des textes officiels d'appellation (INAO, OIV, autorités nationales équivalentes) et de sources terrain vérifiées.",
    method_3: "Les parcours Academy sont conçus par des professionnels certifiés WSET Diploma / sommellerie.",
    method_4: "La date de dernière mise à jour est affichée sur chaque fiche significative.",
    founder_role: "Fondatrice, geoVINUM — DipWSET",
    founder_quote: "« J'ai créé geoVINUM parce que la connaissance du vin mérite une carte, pas une étagère de manuels. Chaque appellation raconte une histoire — je suis là pour vous aider à la lire. »",
    editor_h: "Éditeur : EtOH",
    editor_p_a: "geoVINUM est édité par",
    editor_p_b: "EtOH",
    editor_p_c: ", entreprise spécialisée dans les technologies pour la filière vin (CRM, portefeuille, dégustation, coaching). geoVINUM est le produit d'EtOH dédié à la cartographie, aux données et à l'apprentissage — une entité identifiable à part entière.",
    contact_h: "Contact",
    contact_p_a: "Toute question sur nos cartes, nos données ou un partenariat :",
    contact_wa: "WhatsApp",
    more_h: "Aller plus loin",
    more_faq: "FAQ", more_explorer: "Explorer", more_academy: "Academy", more_shop: "Boutique", more_contact: "Contact",
  },
  en: {
    seo_title: "About geoVINUM",
    seo_desc: "geoVINUM is a wine-mapping, data and learning platform, published by EtOH. Mission, products, methodology and sources.",
    crumb_home: "Home",
    crumb_about: "About",
    eyebrow: "About",
    h1: "geoVINUM — Mapping, data and knowledge of the world's vineyards",
    lede: "geoVINUM is a platform specialized in wine cartography, wine geography, appellations, terroirs, grape varieties and wine learning. We document the world's producing regions by combining maps, editorial factsheets, learning paths and artificial intelligence.",
    mission_h: "Our mission",
    mission_p1: "Make the geography of wine legible for anyone curious — enthusiasts, students (WSET Levels 3, 4, 5 · sommellerie · oenology), professionals (sommeliers, wine merchants, journalists, exporters) and institutions (wine schools, estates, media).",
    mission_p2_a: "We believe we remember better what we see: that's why",
    mission_p2_b: "the map is our entry point",
    mission_p2_c: ", then data and AI extend it.",
    pillars_h: "Our five pillars",
    p_maps_t: "Maps", p_maps_d: "Printable and downloadable wine maps covering the main regions of the world.",
    p_explorer_t: "Explorer", p_explorer_d: "Editorial base: appellation, grape and producer factsheets — source of truth of the geoVINUM graph.",
    p_academy_t: "Academy", p_academy_d: "Learning paths aligned with the WSET and sommellerie reference frameworks.",
    p_ai_t: "AI", p_ai_d: "Conversational assistant specialized in wine, grounded on our data and maps.",
    p_data_t: "Interactive / Data", p_data_d: "Embeddable maps (iframe) and structured data for B2B partners.",
    method_h: "Our methodology",
    method_1: "Every documented appellation has an Explorer factsheet linked to its parent region, subregions, authorized grape varieties and maps.",
    method_2: "Maps are drawn by our cartography team from the official appellation texts (INAO, OIV, equivalent national authorities) and verified field sources.",
    method_3: "Academy paths are designed by professionals certified WSET Diploma / sommellerie.",
    method_4: "The last-updated date is shown on every significant factsheet.",
    founder_role: "Founder, geoVINUM — DipWSET",
    founder_quote: "\u201cI created geoVINUM because wine knowledge deserves a map, not a shelf of textbooks. Every appellation tells a story — I'm here to help you read it.\u201d",
    editor_h: "Publisher: EtOH",
    editor_p_a: "geoVINUM is published by",
    editor_p_b: "EtOH",
    editor_p_c: ", a company specialized in technologies for the wine industry (CRM, cellar, tasting, coaching). geoVINUM is EtOH's product dedicated to mapping, data and learning — an identifiable entity in its own right.",
    contact_h: "Contact",
    contact_p_a: "Any question on our maps, our data or a partnership:",
    contact_wa: "WhatsApp",
    more_h: "Go further",
    more_faq: "FAQ", more_explorer: "Explorer", more_academy: "Academy", more_shop: "Shop", more_contact: "Contact",
  },
  es: {
    seo_title: "Acerca de geoVINUM",
    seo_desc: "geoVINUM es una plataforma de cartografía vitivinícola, datos y aprendizaje del vino, editada por EtOH. Misión, productos, metodología y fuentes.",
    crumb_home: "Inicio",
    crumb_about: "Acerca de",
    eyebrow: "Acerca de",
    h1: "geoVINUM — Cartografía, datos y conocimiento de los viñedos del mundo",
    lede: "geoVINUM es una plataforma especializada en cartografía vitivinícola, geografía del vino, denominaciones, terruños, variedades y aprendizaje del vino. Documentamos las regiones productoras del mundo cruzando mapas, fichas editoriales, itinerarios pedagógicos e inteligencia artificial.",
    mission_h: "Nuestra misión",
    mission_p1: "Hacer legible la geografía del vino para toda persona curiosa — aficionados, estudiantes (WSET Niveles 3, 4, 5 · sommellerie · enología), profesionales (sumilleres, tiendas de vino, periodistas, exportadores) e instituciones (escuelas de vino, bodegas, medios).",
    mission_p2_a: "Creemos que se retiene mejor lo que se ve: por eso",
    mission_p2_b: "el mapa es nuestra puerta de entrada",
    mission_p2_c: ", luego los datos y la IA lo prolongan.",
    pillars_h: "Nuestros cinco pilares",
    p_maps_t: "Maps", p_maps_d: "Mapas vitivinícolas imprimibles y descargables, que cubren las principales regiones del mundo.",
    p_explorer_t: "Explorer", p_explorer_d: "Base editorial: fichas de denominaciones, variedades y productores — fuente de verdad del grafo geoVINUM.",
    p_academy_t: "Academy", p_academy_d: "Itinerarios de aprendizaje alineados con los marcos WSET y sommellerie.",
    p_ai_t: "AI", p_ai_d: "Asistente conversacional especializado en vino, anclado en nuestros datos y mapas.",
    p_data_t: "Interactive / Data", p_data_d: "Mapas integrables (iframe) y datos estructurados para socios B2B.",
    method_h: "Nuestra metodología",
    method_1: "Cada denominación documentada tiene una ficha Explorer vinculada a su región madre, subregiones, variedades autorizadas y mapas.",
    method_2: "Los mapas son dibujados por nuestro equipo cartográfico a partir de los textos oficiales de denominación (INAO, OIV, autoridades nacionales equivalentes) y de fuentes de campo verificadas.",
    method_3: "Los itinerarios Academy son diseñados por profesionales certificados WSET Diploma / sommellerie.",
    method_4: "La fecha de última actualización se muestra en cada ficha significativa.",
    founder_role: "Fundadora, geoVINUM — DipWSET",
    founder_quote: "\u00abCreé geoVINUM porque el conocimiento del vino merece un mapa, no una estantería de manuales. Cada denominación cuenta una historia — estoy aquí para ayudarte a leerla.\u00bb",
    editor_h: "Editor: EtOH",
    editor_p_a: "geoVINUM está editado por",
    editor_p_b: "EtOH",
    editor_p_c: ", empresa especializada en tecnologías para el sector del vino (CRM, cartera, cata, coaching). geoVINUM es el producto de EtOH dedicado a la cartografía, a los datos y al aprendizaje — una entidad identificable por sí misma.",
    contact_h: "Contacto",
    contact_p_a: "Cualquier pregunta sobre nuestros mapas, nuestros datos o un socio:",
    contact_wa: "WhatsApp",
    more_h: "Ir más lejos",
    more_faq: "FAQ", more_explorer: "Explorer", more_academy: "Academy", more_shop: "Tienda", more_contact: "Contacto",
  },
  pt: {
    seo_title: "Sobre geoVINUM",
    seo_desc: "geoVINUM é uma plataforma de cartografia vitícola, dados e aprendizagem do vinho, editada pela EtOH. Missão, produtos, metodologia e fontes.",
    crumb_home: "Início",
    crumb_about: "Sobre",
    eyebrow: "Sobre",
    h1: "geoVINUM — Cartografia, dados e conhecimento dos vinhedos do mundo",
    lede: "A geoVINUM é uma plataforma especializada em cartografia vitícola, geografia do vinho, denominações, terroirs, castas e aprendizagem do vinho. Documentamos as regiões produtoras do mundo cruzando mapas, fichas editoriais, percursos pedagógicos e inteligência artificial.",
    mission_h: "A nossa missão",
    mission_p1: "Tornar a geografia do vinho legível para qualquer pessoa curiosa — apreciadores, estudantes (WSET Níveis 3, 4, 5 · sommellerie · enologia), profissionais (escanções, garrafeiras, jornalistas, exportadores) e instituições (escolas de vinho, quintas, media).",
    mission_p2_a: "Acreditamos que se retém melhor o que se vê: por isso",
    mission_p2_b: "o mapa é a nossa porta de entrada",
    mission_p2_c: ", depois os dados e a IA prolongam-no.",
    pillars_h: "Os nossos cinco pilares",
    p_maps_t: "Maps", p_maps_d: "Mapas vitícolas imprimíveis e transferíveis, cobrindo as principais regiões do mundo.",
    p_explorer_t: "Explorer", p_explorer_d: "Base editorial: fichas de denominações, castas e produtores — fonte de verdade do grafo geoVINUM.",
    p_academy_t: "Academy", p_academy_d: "Percursos de aprendizagem alinhados com os referenciais WSET e sommellerie.",
    p_ai_t: "AI", p_ai_d: "Assistente conversacional especializado em vinho, ancorado nos nossos dados e mapas.",
    p_data_t: "Interactive / Data", p_data_d: "Mapas integráveis (iframe) e dados estruturados para parceiros B2B.",
    method_h: "A nossa metodologia",
    method_1: "Cada denominação documentada tem uma ficha Explorer ligada à sua região-mãe, sub-regiões, castas autorizadas e mapas.",
    method_2: "Os mapas são desenhados pela nossa equipa de cartografia a partir dos textos oficiais de denominação (INAO, OIV, autoridades nacionais equivalentes) e fontes de terreno verificadas.",
    method_3: "Os percursos Academy são concebidos por profissionais certificados WSET Diploma / sommellerie.",
    method_4: "A data da última atualização é apresentada em cada ficha significativa.",
    founder_role: "Fundadora, geoVINUM — DipWSET",
    founder_quote: "\u00abCriei a geoVINUM porque o conhecimento do vinho merece um mapa, não uma estante de manuais. Cada denominação conta uma história — estou aqui para o ajudar a lê-la.\u00bb",
    editor_h: "Editor: EtOH",
    editor_p_a: "A geoVINUM é editada pela",
    editor_p_b: "EtOH",
    editor_p_c: ", empresa especializada em tecnologias para o setor do vinho (CRM, garrafeira, prova, coaching). A geoVINUM é o produto da EtOH dedicado à cartografia, aos dados e à aprendizagem — uma entidade identificável por direito próprio.",
    contact_h: "Contacto",
    contact_p_a: "Qualquer questão sobre os nossos mapas, os nossos dados ou uma parceria:",
    contact_wa: "WhatsApp",
    more_h: "Ir mais longe",
    more_faq: "FAQ", more_explorer: "Explorer", more_academy: "Academy", more_shop: "Loja", more_contact: "Contacto",
  },
};

const AboutPage = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;

  return (
    <Layout testId="page-about">
      <SEO
        title={c.seo_title}
        description={c.seo_desc}
        breadcrumbs={[
          { name: c.crumb_home, url: "/" },
          { name: c.crumb_about, url: "/about" },
        ]}
        jsonLdList={[
          geovinumOrganizationJsonLd(),
          geovinumWebSiteJsonLd(),
        ]}
      />

      <section className="bg-[color:var(--gv-mist)] py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{c.eyebrow}</div>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-5" data-testid="about-title">
            {c.h1}
          </h1>
          <p className="text-[16px] text-[color:var(--gv-ink)]/80 leading-relaxed">
            {c.lede}
          </p>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 space-y-10 text-[15px] text-[color:var(--gv-ink)]/85 leading-relaxed">

          <div>
            <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.mission_h}</h2>
            <p>{c.mission_p1}</p>
            <p className="mt-2">
              {c.mission_p2_a} <strong>{c.mission_p2_b}</strong>{c.mission_p2_c}
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.pillars_h}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Pillar icon={MapPin} title={c.p_maps_t} desc={c.p_maps_d} to="/shop" />
              <Pillar icon={Database} title={c.p_explorer_t} desc={c.p_explorer_d} to="/blog" />
              <Pillar icon={GraduationCap} title={c.p_academy_t} desc={c.p_academy_d} to="/academy" />
              <Pillar icon={Sparkles} title={c.p_ai_t} desc={c.p_ai_d} to="/geovinum-ai" />
              <Pillar icon={Building2} title={c.p_data_t} desc={c.p_data_d} to="/iframe" />
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.method_h}</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>{c.method_1}</li>
              <li>{c.method_2}</li>
              <li>{c.method_3}</li>
              <li>{c.method_4}</li>
            </ul>
          </div>

          <div className="border border-[color:var(--gv-line)] rounded-[5px] p-6 bg-[color:var(--gv-mist)]/40 flex items-start gap-5" data-testid="about-founder-card">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts/ete3quwg_audrey-geovinum.png"
              alt="Audrey Sousa Chaillet"
              className="w-20 h-20 rounded-full object-cover border-[3px] border-[color:var(--gv-pink)] shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-signature text-[46px] leading-[0.9] text-[color:var(--gv-ink)] mb-1">
                Audrey Sousa Chaillet
              </div>
              <div className="text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">
                {c.founder_role}
              </div>
              <p className="text-[14px] text-[color:var(--gv-ink)]/85 leading-relaxed italic">
                {c.founder_quote}
              </p>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.editor_h}</h2>
            <p>
              {c.editor_p_a} <strong>{c.editor_p_b}</strong>{c.editor_p_c}
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.contact_h}</h2>
            <p>
              {c.contact_p_a}
              <a href="mailto:hello@geovinum.io" className="text-[color:var(--gv-pink)] hover:underline"> hello@geovinum.io</a>
              &nbsp;· {c.contact_wa}&nbsp;<a href="https://wa.me/33660235665" className="text-[color:var(--gv-pink)] hover:underline">+33 6 60 23 56 65</a>
            </p>
          </div>

          <div className="pt-6 border-t border-[color:var(--gv-line)]">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">{c.more_h}</div>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
              <li><Link to="/faq" className="text-[color:var(--gv-pink)] hover:underline">{c.more_faq}</Link></li>
              <li><Link to="/blog" className="text-[color:var(--gv-pink)] hover:underline">{c.more_explorer}</Link></li>
              <li><Link to="/academy" className="text-[color:var(--gv-pink)] hover:underline">{c.more_academy}</Link></li>
              <li><Link to="/shop" className="text-[color:var(--gv-pink)] hover:underline">{c.more_shop}</Link></li>
              <li><Link to="/contact" className="text-[color:var(--gv-pink)] hover:underline">{c.more_contact}</Link></li>
            </ul>
          </div>

        </div>
      </section>
    </Layout>
  );
};

const Pillar = ({ icon: Icon, title, desc, to }) => (
  <Link
    to={to}
    className="group block bg-[color:var(--gv-mist)]/50 border border-[color:var(--gv-line)] rounded-[5px] p-4 hover:border-[color:var(--gv-pink)] transition-colors"
    data-testid={`about-pillar-${title.toLowerCase()}`}
  >
    <div className="flex items-center gap-2 text-[color:var(--gv-pink)] mb-1.5">
      <Icon size={16} />
      <span className="font-display text-[15px] text-[color:var(--gv-ink)]">{title}</span>
    </div>
    <div className="text-[13px] text-[color:var(--gv-ink)]/70">{desc}</div>
  </Link>
);

export default AboutPage;
